import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, StyleSheet, Text, ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../../../../../constants/Colors';
import Style from '../../../../../../constants/Style';
import EditSpaceModal from './EditSpaceModal';
import deleteSpace from '../../../../../../requests/Locations/Spaces/DeleteSpace';
import { updateBuildingId } from '../../../../../../redux/selectedBuildingSlice';
import { AppScreen } from '../../../../../../navigation/LinkingConfiguration';
import { UserPermission } from '../../../../../../constants/Types';
import PressableStyled from '../../../../../../components/PressableStyled';
import SpaceIcon from '../../../../../../components/24pxIcons/SpaceIcon';
import { LocationSubtypeTitles, Space } from '../../../../../../components/Map/SetFloorsModal/FloorItem';
import PriorityIndicator from '../../../PriorityIndicator';
import LocationIcon from '../../../../../../components/30pxIcons/LocationIcon';
import EditIcon24 from '../../../../../../components/24pxIcons/EditIcon24';
import DeleteIcon24 from '../../../../../../components/24pxIcons/DeleteIcon24';

interface SpaceItemProps {
  parentName: string,
  space: Space,
  removeSpace: Function,
  updateSpace,
  setSelectedSpaceId: Dispatch<SetStateAction<string>>,
  showScreenAlert: Function,
  floorId: string,
  buildingId: string,
}

const SpaceItem = ({
  space, parentName, floorId, removeSpace, updateSpace, setSelectedSpaceId, showScreenAlert,
  buildingId,
}: SpaceItemProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const [loading, setLoading] = useState<boolean>(false);
  const [editSpaceModalShown, setEditModalShown] = useState<boolean>(false);
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const showEditSpaceModal: () => void = () => setEditModalShown(true);

  const hideEditSpaceModal: () => void = () => setEditModalShown(false);

  const deleteSpaceError: Function = (errorText?: string) => showScreenAlert(
    'Error',
    errorText
    ?? 'Could not delete space at the moment. Please try again later.',
  );

  const remove: () => void = () => showScreenAlert(
    'Delete space?',
    <Text>
      {'Do you want to delete '}
      <Text style={Style.alertBoldText}>{space.Name}</Text>
      {' from '}
      <Text style={Style.alertBoldText}>{parentName}</Text>
      {'?'}
    </Text>,
    true,
    () => {
      setLoading(true);
      deleteSpace(space.LocationId).then((res) => {
        if (res && !res.isError) {
          removeSpace(space.LocationId);
          showScreenAlert(
            `Deleted ${space.Name}`,
            <Text>
              <Text style={Style.alertBoldText}>{space.Name}</Text>
              {' was successfully deleted from '}
              <Text style={Style.alertBoldText}>{parentName}</Text>
            </Text>,
            false,
          );
        } else deleteSpaceError(res?.errorText);
        setLoading(false);
      }).catch((error) => {
        deleteSpaceError(error?.message);
        setLoading(false);
      });
    },
    'Delete',
  );

  const navigateToMap: () => void = () => {
    dispatch(updateBuildingId(buildingId));
    const areaId: string = space.ParentId;
    const spaceId: string = space.LocationId;
    navigation.navigate(AppScreen.MAP as any, { floorId, areaId, spaceId });
  };

  return <>
    <PressableStyled
      style={[Style.row, styles.container]}
      onPress={() => setSelectedSpaceId(space.LocationId)}
    >
      <View style={[Style.row, styles.titleSection]}>
        <View style={styles.iconContainer}>
          <SpaceIcon />
        </View>
        <View>
          <Text style={styles.title}>{space.Name}</Text>
          {LocationSubtypeTitles[space.LocationSubtype]?.length > 0
            && <Text style={styles.label}>{LocationSubtypeTitles[space.LocationSubtype]}</Text>}
        </View>
      </View>
      <View style={[Style.row, styles.prioritySection]}>
        <PriorityIndicator isHighPriority={space.IsHighPriority} />
      </View>
      <View style={styles.planSection}>
        <Text style={styles.title}>{space.assetCount ?? 0}</Text>
      </View>
      {happyEnabled && <View style={styles.planSection}>
        <Text style={styles.title}>{(space.sensorCount ?? 0) + (space.meterCount ?? 0)}</Text>
      </View>}
      <View style={[Style.row, styles.actionSection]}>
        <PressableStyled
          onPress={navigateToMap}
          testID='space-navigate-to-map-btn'
        >
          <LocationIcon color={Colors.light.hereworksBlue500} />
        </PressableStyled>
        {canUpdateLocations && <PressableStyled
          onPress={showEditSpaceModal}
          testID='edit-space-btn'
        >
          <EditIcon24 style={styles.editIcon} />
        </PressableStyled>}
        {canDeleteLocations && <PressableStyled
          onPress={remove}
          testID='delete-space-btn'
        >
          <DeleteIcon24 />
        </PressableStyled>}
      </View>
    </PressableStyled>
    <ActivityIndicator
      animating={loading}
      size='large'
      style={Style.activityIndicator}
    />
    <EditSpaceModal
      {...{
        space,
        removeSpace,
        updateSpace,
        parentName,
        showScreenAlert,
        editSpaceModalShown,
        hideEditSpaceModal,
      }}
    />
  </>;
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.light.hereworksWhite100,
    borderRadius: 10,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  titleSection: {
    flex: 0.51,
    justifyContent: 'flex-start',
  },
  prioritySection: {
    flex: 0.12,
  },
  planSection: { flex: 0.09 },
  actionSection: {
    flex: 0.2,
    textAlign: 'right',
    justifyContent: 'flex-end',
  },
  iconContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    aspectRatio: 1,
    borderRadius: 20,
    backgroundColor: Colors.light.hereworksDarkBlue,
    marginEnd: 12,
  },
  title: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_600SemiBold',
    color: Colors.light.hereworksBlack600,
  },
  number: {
    fontSize: 12,
    lineHeight: 15,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  editIcon: {
    marginHorizontal: 12,
  },
  label: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
});

export default SpaceItem;
